/**
 * Network Statistics Module
 * Fetches and displays network statistics from ipgeo.csv
 */

import Papa from 'papaparse';
import type { NetworkStats } from '../types/ipgeo';

export async function initializeStats(): Promise<void> {
  try {
    // Fetch the CSV file
    const response = await fetch('/ipgeo.csv');
    if (!response.ok) {
      throw new Error(`Failed to fetch ipgeo.csv: ${response.statusText}`);
    }

    const csvText = await response.text();

    // Parse CSV using PapaParse
    const parsed = Papa.parse(csvText, {
      skipEmptyLines: true,
      comments: '#'
    }) as Papa.ParseResult<string[]>;

    if (!parsed.data || parsed.data.length === 0) {
      throw new Error('No data found in ipgeo.csv');
    }

    // Calculate statistics
    const stats = calculateStats(parsed.data);

    // Update the UI
    updateStatsUI(stats);

    console.log('Statistics loaded successfully:', stats);
  } catch (error) {
    console.error('Error loading statistics:', error);
    showStatsError();
  }
}

/**
 * Calculate statistics from parsed CSV data
 */
function calculateStats(data: string[][]): NetworkStats {
  const subnets: string[] = [];
  const cities = new Set<string>();
  const countries = new Set<string>();
  const regions = new Set<string>();

  // Process each row
  data.forEach(row => {
    if (row.length >= 4 && row[0]) {
      // row[0] = subnet, row[1] = country, row[2] = region, row[3] = city
      subnets.push(row[0]);

      if (row[1]) countries.add(row[1]);
      if (row[2]) regions.add(row[2]);
      if (row[3]) cities.add(row[3]);
    }
  });

  return {
    totalSubnets: subnets.length,
    uniqueCities: cities.size,
    countries: countries.size,
    regions: regions.size,
    countryList: Array.from(countries),
    cityList: Array.from(cities),
    regionList: Array.from(regions)
  };
}

/**
 * Update the statistics UI with calculated values
 */
function updateStatsUI(stats: NetworkStats): void {
  // Update subnet count
  const statSubnets = document.getElementById('statSubnets');
  if (statSubnets) {
    statSubnets.textContent = stats.totalSubnets.toString();
  }

  // Update location count
  const statLocations = document.getElementById('statLocations');
  if (statLocations) {
    statLocations.textContent = stats.uniqueCities.toString();
  }

  // Update country count
  const statCountries = document.getElementById('statCountries');
  if (statCountries) {
    statCountries.textContent = stats.countries.toString();
  }

  // Peering count is static (3) and already set in HTML
}

/**
 * Show error state in stats UI
 */
function showStatsError(): void {
  const elements = ['statSubnets', 'statLocations', 'statCountries'];
  elements.forEach(id => {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = 'N/A';
      element.style.color = '#f59e0b'; // warning color
    }
  });
}

/**
 * Format large numbers for display
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}
